// Política de EMISIÓN AUTOMÁTICA por flujo (e-commerce, teleconsulta, signage).
// Se monta dentro de Financiero → Configuración de Emisión.
// Apagado por defecto: los flujos que facturan solos dejan la factura en borrador
// hasta que un administrador los habilite aquí, a conciencia.
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

interface Flujo {
  origen: string; permitido: boolean;
  ambiente: string | null; ambiente_efectivo: string; actualizado: string | null;
}
interface Politica { ambiente_oficina: string; flujos: Flujo[] }

const ETIQUETA: Record<string, { titulo: string; detalle: string }> = {
  ecommerce: {
    titulo: 'Tienda en línea (e-commerce)',
    detalle: 'Factura al confirmarse el pago de un pedido web. Apagado, el pedido queda pagado y su factura en borrador.',
  },
  teleconsulta: {
    titulo: 'Teleconsulta médica',
    detalle: 'Factura al confirmar el pago de la consulta. Apagado, la factura queda en borrador para emitirla luego.',
  },
  signage: {
    titulo: 'Pauta publicitaria (cartelería)',
    detalle: 'Factura al facturar un contrato de anunciante. Apagado, deja la factura lista para emitir a mano.',
  },
};

export function EmisionAutomatica() {
  const qc = useQueryClient();
  const { data } = useQuery<Politica>({
    queryKey: ['fin-emision-auto'],
    queryFn: async () => (await apiClient.post('/rpc/fe_emision_auto_listar', {}, FIN)).data,
  });

  const guardar = useMutation({
    mutationFn: (p: { origen: string; permitido: boolean; ambiente: string | null }) =>
      apiClient.post('/rpc/fe_emision_auto_set',
        { _origen: p.origen, _permitido: p.permitido, _ambiente: p.ambiente }, FIN),
    onSuccess: () => { toast.success('Política actualizada'); void qc.invalidateQueries({ queryKey: ['fin-emision-auto'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!data) return null;
  const produccion = data.ambiente_oficina === '2';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Emisión automática al SRI
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Estos flujos pueden emitir comprobantes electrónicos sin que nadie pulse «Emitir».
          Vienen <b>apagados</b>: con el interruptor en «No», la factura se crea en borrador y se emite después
          desde Facturación. Cada flujo puede además emitir en el ambiente de <b>pruebas</b> aunque la oficina
          esté en producción — así se prueba de punta a punta sin generar comprobantes reales.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {produccion && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>La oficina está en <b>PRODUCCIÓN</b>: todo flujo que habilites con ambiente heredado
              emitirá comprobantes <b>reales y con validez tributaria</b>.</span>
          </div>
        )}

        {data.flujos.map((f) => (
          <div key={f.origen} className="rounded-md border p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium">{ETIQUETA[f.origen]?.titulo ?? f.origen}</div>
                <div className="text-xs text-muted-foreground">{ETIQUETA[f.origen]?.detalle}</div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${f.permitido
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-muted text-muted-foreground'}`}>
                {f.permitido ? 'Emite automáticamente' : 'Deja la factura en borrador'}
              </span>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor={`auto-${f.origen}`}>¿Emite al SRI sola?</label>
                <select id={`auto-${f.origen}`} className="h-9 rounded-md border bg-background px-2 text-sm"
                  value={f.permitido ? 'si' : 'no'}
                  onChange={(e) => guardar.mutate({ origen: f.origen, permitido: e.target.value === 'si', ambiente: f.ambiente })}>
                  <option value="no">No — solo crea el borrador</option>
                  <option value="si">Sí — emite automáticamente</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor={`amb-${f.origen}`}>Ambiente de este flujo</label>
                <select id={`amb-${f.origen}`} className="h-9 rounded-md border bg-background px-2 text-sm"
                  value={f.ambiente ?? ''}
                  onChange={(e) => guardar.mutate({ origen: f.origen, permitido: f.permitido, ambiente: e.target.value || null })}>
                  <option value="">Hereda el de la oficina ({data.ambiente_oficina === '2' ? 'producción' : 'pruebas'})</option>
                  <option value="1">Pruebas (certificación)</option>
                  <option value="2">Producción</option>
                </select>
              </div>
              <div className="text-xs text-muted-foreground">
                Emitirá en: <b>{f.ambiente_efectivo === '2' ? 'PRODUCCIÓN' : 'pruebas'}</b>
                {f.actualizado && <> · cambiado {new Date(f.actualizado).toLocaleString(LOCALE, { hour12: false })}</>}
              </div>
            </div>
          </div>
        ))}

        <p className="text-xs text-muted-foreground">
          Si la emisión automática falla, la factura queda marcada con el error y aparece en
          Financiero → Gestión SRI → Monitor de Emisiones, donde se puede reintentar.
        </p>
      </CardContent>
    </Card>
  );
}
